import {
	Box,
	Button,
	CardActions,
	CardContent,
	FormHelperText,
	Grid,
	Tab,
	Tabs,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useController,
	useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import AppTextField, {
	InputType,
} from '@/common/components/form-inputs/AppTextField';
import { useNavigate, useParams } from 'react-router-dom';
import { FormType } from '@/api/enum/formType.enum';
import {
	Dispatch,
	SetStateAction,
	useCallback,
	useEffect,
	useRef,
	useState,
} from 'react';
import CloseIcon from '@mui/icons-material/Close';

import { enqueueSnackbar, closeSnackbar } from 'notistack';
import VisuallyHiddenInput from '@/common/components/UploadInput';
import { useFileUploader } from '@/hooks/useFileUploader';
import { ExerciseTagDTO, FileInfo } from '@/api/DTO/common.interface';
import LoopOutlinedIcon from '@mui/icons-material/LoopOutlined';
import DriveFolderUploadOutlinedIcon from '@mui/icons-material/DriveFolderUploadOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { GameCartoonStickerTreasureDTO } from '@/api/DTO/treasury/chartlet.interface';
import AppDropDownMenu from '@/common/components/form-inputs/AppDropDownMenu';
import {
	useCreateTreasureMutation,
	useGetChartletDetailQuery,
	useUpdateTreasureDetailMutation,
} from '@/api/treasury/chartletApiSlice';
import { ChartletType, QualityType } from '@/api/enum/common.enum';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

interface Props {
	type: FormType;
	details?: GameCartoonStickerTreasureDTO;
	setType: Dispatch<SetStateAction<FormType>>;
}

export default function SingleInfoForm({ type, setType, details }: Props) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [needConfirm, setNeedConfirm] = useState(false);
	const [currentTab, setCurrentTab] = useState('zh');
	const [createTreasure] = useCreateTreasureMutation();
	const [updateTreasureDetail] = useUpdateTreasureDetailMutation();
	const { uploadFile } = useFileUploader();
	const [imageFile, setImageFile] = useState<FileInfo | null>(null);
	const { chartletId, chartletSingleId } = useParams();
	const imageInputRef = useRef<HTMLInputElement>(null);

	const { data: chartletDetails } = useGetChartletDetailQuery(
		chartletId || '',
		{
			refetchOnMountOrArgChange: true,
		}
	);

	const qualityList = [
		{ id: QualityType.Normal, name: t('chartletList.normal') },
		{ id: QualityType.Rare, name: t('chartletList.rare') },
		{ id: QualityType.Precious, name: t('chartletList.precious') },
		{ id: QualityType.SuperPrecious, name: t('chartletList.superPrecious') },
	];

	const defaultValues: DefaultValues<GameCartoonStickerTreasureDTO> = {};
	useEffect(() => {
		// console.log(details);
		if (details?.titleEn) methods.setValue('titleEn', details.titleEn);
		if (details?.titleZh) methods.setValue('titleZh', details.titleZh);
		if (details?.publishedStartAt)
			methods.setValue('publishedStartAt', details.publishedStartAt);
		if (details?.publishedEndAt)
			methods.setValue('publishedEndAt', details.publishedEndAt);
		if (details?.costPoints) methods.setValue('costPoints', details.costPoints);
		if (Number(details?.quality) > -1)
			methods.setValue('quality', details?.quality);

		if (details?.treasurePic) {
			setImageFile(details.treasurePic);
			methods.setValue('cover', 'file');
		}
	}, [details]);

	const formSchema = Yup.object().shape({
		titleZh: Yup.string().required(t('errorMessage.required')),
		costPoints: Yup.string().required(t('errorMessage.required')),
		quality: Yup.string().required(t('errorMessage.required')),

		cover: Yup.string().required(t('errorMessage.pleaseSelect')),
	});
	const methods = useForm<GameCartoonStickerTreasureDTO>({
		defaultValues,
		resolver: yupResolver(formSchema),
	});
	const coverField = useController({
		name: 'cover',
		control: methods.control,
	});

	useEffect(() => {
		const subscription = methods.watch((value) => {
			if (!needConfirm) setNeedConfirm(true);
		});

		return () => subscription.unsubscribe();
	}, [methods.watch]);

	const onSubmit: SubmitHandler<GameCartoonStickerTreasureDTO> = async (
		data
	) => {
		const response =
			type == FormType.create
				? await createTreasure({
						...data,
						treasurePic: imageFile,
						chartletId: chartletId,
				  })
				: await updateTreasureDetail({
						...data,
						treasurePic: imageFile,
						id: chartletSingleId || '',
						seriesId: chartletId,
				  });
		if ('data' in response) {
			enqueueSnackbar(
				type == FormType.create
					? t('snackbar.addTreasureSuccess')
					: t('snackbar.changeConentSuccess'),
				{
					variant: 'success',
					anchorOrigin: { horizontal: 'center', vertical: 'top' },
					autoHideDuration: 3000,
					action: (key) => {
						return <CloseIcon onClick={() => closeSnackbar(key)} />;
					},
				}
			);
			if (type == FormType.create) {
				navigate(`/treasury-manage/chartlet-list/info/${chartletId}`);
			} else {
				setType(FormType.view);
			}
		}
	};

	const handleClose = () => {
		if (type == FormType.edit) return setType(FormType.view);
		navigate(`/treasury-manage/chartlet-list/info/${chartletId}`);
	};

	const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
		setCurrentTab(newValue);
	};

	const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
		const selectedFile = event.target.files?.[0];
		if (!selectedFile) return;

		if (selectedFile && !selectedFile.name.toLowerCase().endsWith('.webp')) {
			imageInputRef.current && (imageInputRef.current.value = '');
			return enqueueSnackbar(t('snackbar.imageWebp'), {
				variant: 'error',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
		}

		if (Number(chartletDetails?.data.stickerType) == ChartletType.Dynamic) {
			if (selectedFile.size > 500 * 1024) {
				imageInputRef.current && (imageInputRef.current.value = '');
				return enqueueSnackbar(t('snackbar.imageSize<500kb'), {
					variant: 'error',
					anchorOrigin: { horizontal: 'center', vertical: 'top' },
					autoHideDuration: 3000,
					action: (key) => {
						return <CloseIcon onClick={() => closeSnackbar(key)} />;
					},
				});
			}
		} else {
			if (selectedFile.size > 100 * 1024) {
				imageInputRef.current && (imageInputRef.current.value = '');
				return enqueueSnackbar(t('snackbar.imageSize<100kb'), {
					variant: 'error',
					anchorOrigin: { horizontal: 'center', vertical: 'top' },
					autoHideDuration: 3000,
					action: (key) => {
						return <CloseIcon onClick={() => closeSnackbar(key)} />;
					},
				});
			}
		}

		const reader = new FileReader();
		reader.onload = (e) => {
			const img = new Image();
			img.onload = async () => {
				console.log(img.width);
				if (img.width !== 512 || img.height !== 512) {
					imageInputRef.current && (imageInputRef.current.value = '');
					return enqueueSnackbar(t('snackbar.imageSize512'), {
						variant: 'error',
						anchorOrigin: { horizontal: 'center', vertical: 'top' },
						autoHideDuration: 3000,
						action: (key) => {
							return <CloseIcon onClick={() => closeSnackbar(key)} />;
						},
					});
				}

				const res = await uploadFile(selectedFile);
				if (res) {
					setImageFile(res);
					methods.setValue('cover', res.name);

					methods.trigger('cover');
				}
			};
			img.src = e.target?.result as string; // 觸發圖片加載
		};
		reader.readAsDataURL(selectedFile);
	};

	const handleDeleteFile = () => {
		setImageFile(null);
		methods.setValue('cover', '');
		imageInputRef.current && (imageInputRef.current.value = '');
	};

	return (
		<>
			<FormProvider {...methods}>
				<form onSubmit={methods.handleSubmit(onSubmit)}>
					<CardContent sx={{ padding: '24px' }}>
						<Tabs value={currentTab} onChange={handleTabChange}>
							<Tab
								label={
									<Typography variant='body1'>{t('meditation.zh')}</Typography>
								}
								value='zh'
							/>
							<Tab
								label={
									<Typography variant='body1'>{t('meditation.en')}</Typography>
								}
								value='en'
							/>
						</Tabs>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							{currentTab == 'zh' && (
								<>
									<Grid item xs={12}>
										<AppTextField
											control={methods.control}
											name='titleZh'
											label={t('chartletList.treasureTitle')}
										/>
									</Grid>
								</>
							)}
							{currentTab == 'en' && (
								<>
									<Grid item xs={12}>
										<AppTextField
											control={methods.control}
											name='titleEn'
											label={t('chartletList.treasureTitle')}
										/>
									</Grid>
								</>
							)}
							<Grid item xs={6}>
								<AppDropDownMenu
									control={methods.control}
									name={'quality'}
									label={t('chartletList.quality')}
									options={qualityList}
									required
								/>
							</Grid>
							<Grid item xs={6}>
								<AppTextField
									control={methods.control}
									name='costPoints'
									label={t('chartletList.integral')}
									inputType={InputType.number}
								/>
							</Grid>

							<Grid item xs={6}>
								<Typography variant='body2' color='common.neutral40'>
									{t('chartletList.treasureImage')}
								</Typography>
								{imageFile && (
									<img
										src={imageFile?.url || ''}
										width={245}
										height={245}
										style={{
											objectFit: 'contain',
										}}
									/>
								)}

								<Box display={'flex'} alignItems={'center'}>
									{imageFile ? (
										<Button
											component='label'
											sx={{ height: 24 }}
											endIcon={<LoopOutlinedIcon />}
										>
											{t('information.changeImage')}
											<VisuallyHiddenInput
												type='file'
												name='thumbnail'
												accept='image/*'
												ref={imageInputRef}
												onChange={(event) => {
													handleUpload(event);
												}}
											/>
										</Button>
									) : (
										<Button
											component='label'
											sx={{ height: 24 }}
											endIcon={<DriveFolderUploadOutlinedIcon />}
										>
											{t('information.uploadImage')}
											<VisuallyHiddenInput
												type='file'
												name='thumbnail'
												accept='image/webp'
												ref={imageInputRef}
												onChange={(event) => {
													handleUpload(event);
												}}
											/>
										</Button>
									)}
									{imageFile && (
										<Button
											component='label'
											sx={{ height: 24, marginLeft: '24px' }}
											color='error'
											endIcon={<DeleteOutlinedIcon />}
											onClick={() => {
												handleDeleteFile();
											}}
										>
											{t('information.deleteImage')}
										</Button>
									)}
								</Box>
								<FormHelperText color='error' style={{ color: '#d32f2f' }}>
									{coverField.fieldState.error?.message}
								</FormHelperText>
							</Grid>
							<Grid item xs={12}>
								<Box
									display='flex'
									justifyContent='space-between'
									alignItems='center'
									marginBottom={'10px'}
								>
									<Typography
										variant='body2'
										sx={{
											display: 'flex',
										}}
									>
										<ErrorOutlineIcon sx={{ fontSize: '20px' }} />
										&nbsp;
										{Number(chartletDetails?.data.stickerType) ==
										ChartletType.Dynamic
											? t('chartletList.dynamicTips')
											: t('chartletList.staticTips')}
									</Typography>
								</Box>
							</Grid>
						</Grid>
					</CardContent>
					<CardActions sx={{ justifyContent: 'end', padding: '24px' }}>
						<Button onClick={handleClose}>{t('button.cancel')}</Button>
						<Button
							type='submit'
							variant='contained'
							disabled={!needConfirm && type === FormType.create}
						>
							{type === FormType.edit ? t('button.save') : t('button.add')}
						</Button>
					</CardActions>
				</form>
			</FormProvider>
		</>
	);
}
